import React, { useContext, useEffect, useMemo, useState } from 'react';
import PageHeader from 'Components/PageHeader';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { SbButton, SbSelect, SbTabs, SbTooltip } from 'uiCore';
import { AuthContext } from 'State/AuthContext';
import useRoleReporting from './useRoleReporting';
import RoleReportingTable from './RoleReportingTable';
import RoleReportingBulkUpload from './RoleReportingBulkUpload';
import {
  PageContainer,
} from '../CompensationManagement/CompensationManagement.styles';
import {
  RoleReportingSearchWrapperStyled,
  RoleReportingContentSectionStyled,
  RoleReportingTabsRowStyled,
} from './RoleReporting.styles';

const SEARCH_FIELDS = ['job_title', 'job_code', 'sb_requisition_number'];
const TOOLTIP_SEARCH = 'Search job descriptions by job title, job code, or requisition number.';

const ROLE_REPORTING_TABS = [
  { key: 'single-update', label: 'Single Update' },
  { key: 'bulk-update', label: 'Bulk Update' },
];

const RoleReporting = () => {
  const navigate = useNavigate();
  const { email: contextEmail } = useContext(AuthContext);
  const email = contextEmail || localStorage.getItem('username') || '';

  const [activeTab, setActiveTab] = useState('single-update');
  const [tableFilters, setTableFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const {
    allData,
    loading,
    fetchData,
    filterOptions,
    managerOptionsFor,
    editingRowId,
    editValues,
    savingRowId,
    handleEditStart,
    handleEditCancel,
    handleManagerChange,
    handleApexChange,
    handleEditSave,
  } = useRoleReporting(email);

  useEffect(() => {
    if (email) fetchData();
  }, [email, fetchData]);

  const searchOptions = useMemo(() => {
    const unique = new Set();
    allData.forEach((row) =>
      SEARCH_FIELDS.forEach((f) => {
        const v = row[f];
        if (v && typeof v === 'string' && v.trim()) unique.add(v);
      }),
    );
    return Array.from(unique).map((v) => ({ value: v, label: v }));
  }, [allData]);

  const filteredData = useMemo(() => {
    const term = (searchTerm ?? '').trim().toLowerCase();
    if (!term) return allData;
    return allData.filter((row) =>
      SEARCH_FIELDS.some((f) => row[f]?.toString().toLowerCase().includes(term)),
    );
  }, [allData, searchTerm]);

  const handleTableChange = (_pagination, filters) => {
    setTableFilters(filters || {});
    handleEditCancel();
  };

  return (
    <PageContainer>
      <PageHeader
        title="Role Reporting"
        subtitle="Assign reporting managers and mark Apex Roles across your active job descriptions"
        onBack={() => navigate('/admin-setup')}
        backLabel="Back to Admin Setup"
        actions={
          activeTab === 'single-update' ? (
            <SbTooltip content={TOOLTIP_SEARCH}>
              <RoleReportingSearchWrapperStyled>
                <SbSelect
                  options={searchOptions}
                  value={searchTerm || undefined}
                  suffixIcon={<SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />}
                  onChange={(v) => setSearchTerm(v ?? '')}
                  placeholder="Search job title, code, requisition…"
                  allowClear
                  showSearch
                  notFoundContent="No matching results"
                  listHeight={300}
                />
              </RoleReportingSearchWrapperStyled>
            </SbTooltip>
          ) : null
        }
      />

      <RoleReportingTabsRowStyled>
        <SbTabs activeKey={activeTab} onChange={setActiveTab} items={ROLE_REPORTING_TABS} />
      </RoleReportingTabsRowStyled>

      <RoleReportingContentSectionStyled>
        {activeTab === 'single-update' ? (
          <RoleReportingTable
            dataSource={filteredData}
            loading={loading}
            tableFilters={tableFilters}
            onTableChange={handleTableChange}
            filterOptions={filterOptions}
            managerOptionsFor={managerOptionsFor}
            editingRowId={editingRowId}
            editValues={editValues}
            onEditStart={handleEditStart}
            onEditCancel={handleEditCancel}
            onManagerChange={handleManagerChange}
            onApexChange={handleApexChange}
            onEditSave={handleEditSave}
            savingRowId={savingRowId}
          />
        ) : (
          <RoleReportingBulkUpload onSubmitSuccess={fetchData} onContinue={() => setActiveTab('single-update')} />
        )}
      </RoleReportingContentSectionStyled>
    </PageContainer>
  );
};

export default RoleReporting;
